import { supabase } from './supabase';
import type { Reservation } from '../types';
import type { B2BBuyer } from '../types/b2b';
import { sendEdgeEmail, type EmailAttachment } from './b2bEmail';
import {
  generateReservationHTML,
  htmlToPdfBase64,
  type SupplierProfile,
} from './pdfGenerator';
import { filterCcVsPrimary } from '../utils/emailRecipients';

const SUPPLIER_FIELDS =
  'company_name, legal_entity_name, company_street, company_house_number, company_postal_code, company_city, company_country, company_address, company_phone, company_email, company_website, kvk_number, vat_number, company_tax_id';

function pseudoBuyerFromReservation(reservation: Reservation, adminId: string): B2BBuyer {
  const now = new Date().toISOString();
  const name = reservation.customer_name?.trim() || 'Customer';
  return {
    id: reservation.id,
    admin_id: adminId,
    buyer_code: 'POS',
    email: reservation.customer_email?.trim() || `${reservation.admin_id.slice(0, 8)}@internal.local`,
    legal_entity_name: name,
    trade_name: name,
    contact_person_name: name,
    billing_address: reservation.customer_address,
    billing_city: '',
    billing_postal_code: '',
    billing_country: undefined,
    shipping_address: reservation.customer_address,
    preferred_language: 'en',
    preferred_currency: 'EUR',
    payment_terms: '',
    credit_limit: 0,
    account_discount: 1,
    vat_percentage: 21,
    account_status: 'active',
    is_active: true,
    created_at: now,
    updated_at: now,
  };
}

/**
 * Sends configured staff CC addresses a reservation summary email with a reservation PDF attachment.
 * Intended for reservations created outside the B2B portal (POS / In-house). B2B reservations are
 * handled by B2BShopPage which CCs staff on the buyer confirmation instead.
 *
 * Does not pass reservation_id to the edge function so buyer-facing email_* columns are unchanged.
 */
export async function notifyStaffCcForPosReservation(adminId: string, reservation: Reservation): Promise<void> {
  const r = reservation as Reservation & {
    buyer_id?: string | null;
    reservation_source?: string | null;
    pickup_scheduled_date?: string | null;
  };
  if (r.buyer_id || r.reservation_source === 'b2b') {
    return;
  }

  const { data: userRow } = await supabase
    .from('users')
    .select('reservation_pdf_cc_emails')
    .eq('id', adminId)
    .single();

  const raw = Array.isArray(userRow?.reservation_pdf_cc_emails)
    ? (userRow!.reservation_pdf_cc_emails as string[]).filter(
        e => typeof e === 'string' && e.trim().length > 0,
      )
    : [];
  const buyerEmail =
    reservation.customer_email?.trim() || '';
  const ccAll = filterCcVsPrimary(raw, buyerEmail);

  if (ccAll.length === 0) return;

  const { data: adminUser } = await supabase
    .from('users')
    .select(SUPPLIER_FIELDS)
    .eq('id', adminId)
    .single();

  const supplier: SupplierProfile = {
    company_name: adminUser?.company_name,
    legal_entity_name: adminUser?.legal_entity_name,
    company_street: adminUser?.company_street,
    company_house_number: adminUser?.company_house_number,
    company_postal_code: adminUser?.company_postal_code,
    company_city: adminUser?.company_city,
    company_country: adminUser?.company_country,
    company_address: adminUser?.company_address,
    company_phone: adminUser?.company_phone,
    company_email: adminUser?.company_email,
    company_website: adminUser?.company_website,
    kvk_number: adminUser?.kvk_number,
    vat_number: adminUser?.vat_number,
    company_tax_id: adminUser?.company_tax_id,
  };

  const items = reservation.reservation_items || [];
  const productIds = [...new Set(items.map(i => i.product_id).filter(Boolean))];

  let productMap: Record<
    string,
    { id: string; name: string; sku?: string; color_id?: string; size_id?: string }
  > = {};
  if (productIds.length > 0) {
    const { data: prods } = await supabase
      .from('products')
      .select('id, name, sku, color_id, size_id')
      .in('id', productIds);
    (prods || []).forEach((p: any) => {
      productMap[p.id] = p;
    });
  }

  let colorMap: Record<string, string> = {};
  let sizeMap: Record<string, string> = {};
  const [colorsRes, sizesRes] = await Promise.all([
    supabase.from('product_colors').select('id, name').eq('admin_id', adminId),
    supabase.from('product_sizes').select('id, name').eq('admin_id', adminId),
  ]);
  (colorsRes.data || []).forEach((c: any) => {
    colorMap[c.id] = c.name;
  });
  (sizesRes.data || []).forEach((s: any) => {
    sizeMap[s.id] = s.name;
  });

  const pdfLines = items.map(it => {
    const p = productMap[it.product_id];
    const colorName = p?.color_id ? colorMap[p.color_id] || '' : '';
    const sizeName = p?.size_id ? sizeMap[p.size_id] || '' : '';
    return {
      name: p?.name?.trim() || 'Item',
      sku: p?.sku,
      color_name: colorName || undefined,
      size_name: sizeName || undefined,
      quantity: it.quantity,
      unit_price: it.unit_price,
      total_price: it.total_price,
    };
  });

  const pickupScheduled =
    r.pickup_scheduled_date || reservation.pickup_date || undefined;

  const resForPdf = {
    reservation_number: reservation.reservation_number,
    created_at: reservation.created_at || reservation.reserved_at,
    total_amount: Number(reservation.total_amount) || 0,
    ...(pickupScheduled ? { pickup_scheduled_date: pickupScheduled } : {}),
  };

  const pseudoBuyer = pseudoBuyerFromReservation(reservation, adminId);
  const htmlPdf = generateReservationHTML(resForPdf as any, pseudoBuyer, pdfLines as any, supplier);

  let pdfBase64: string;
  try {
    pdfBase64 = await htmlToPdfBase64(htmlPdf, { scale: 1.5, jpegQuality: 0.85 });
  } catch (e) {
    console.error('Reservation PDF generation failed:', e);
    pdfBase64 = '';
  }

  const attachments: EmailAttachment[] =
    pdfBase64.length > 0
      ? [
          {
            filename: `Reservation-${reservation.reservation_number}.pdf`,
            content: pdfBase64,
            contentType: 'application/pdf',
            encoding: 'base64',
          },
        ]
      : [];

  const pickupLine = pickupScheduled
    ? `<p style="margin:0 0 8px;"><strong>Pickup / note:</strong> ${new Date(pickupScheduled).toLocaleDateString(
        'en-GB',
      )}</p>`
    : '';

  const bodyHtml = `
    <div style="font-family:Arial,sans-serif;max-width:640px;color:#111827;">
      <p style="margin:0 0 16px;"><strong>New reservation created</strong> (POS / in-house).</p>
      <p style="margin:0 0 8px;"><strong>${reservation.reservation_number}</strong></p>
      <p style="margin:0 0 8px;">Customer / reserved for: ${escapeHtml(reservation.customer_name || '—')}</p>
      ${pickupLine}
      ${reservation.notes?.trim()
        ? `<p style="margin:0 0 8px;"><strong>Notes:</strong> ${escapeHtml(reservation.notes)}</p>`
        : ''}
      <p style="margin:0 0 8px;">Total: €${Number(reservation.total_amount).toFixed(2)}</p>
      <p style="margin:16px 0 0;font-size:13px;color:#6b7280;">The reservation PDF is attached.</p>
    </div>
  `;

  const primaryTo = ccAll[0]!;
  const staffCcTail = ccAll.length > 1 ? ccAll.slice(1) : [];

  await sendEdgeEmail({
    to: primaryTo,
    cc: staffCcTail.length > 0 ? staffCcTail : undefined,
    subject: `Reservation ${reservation.reservation_number} (${reservation.customer_name || 'Customer'})`,
    html: bodyHtml,
    attachments: attachments.length > 0 ? attachments : undefined,
    adminId,
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
